/**
 * clasificadores/publicaciones.js — Clasificador puro del dashboard Publicaciones
 *
 * Réplica del modelo de Publicidad (docs/automatizacion-deteccion-tareas.md):
 * función PURA — no toca red ni Firestore. La comparten la vista manual
 * "🎯 Detección de tareas" (PublicacionesDeteccionTareas) y el agente
 * ⭐ Auto-detección (mismo input, mismos hallazgos, paridad garantizada).
 *
 * 3 secciones (catálogo v1, 11-jul-2026) — las reglas son ESPEJO de
 * `detectarMetricas` de PublicacionesPage (misma fuente de verdad conceptual):
 *
 *   agregar_cuotas   → producto (MLAU) sin ninguna cuota activa pero cuya
 *                      categoría × listing_type SÍ permite cuotas (grupo
 *                      'disp' del modelo Tiene/Puede/No puede, ADR 012).
 *                      1 hallazgo por MLAU (la hermana activa representativa).
 *   mejorar_calidad  → score de salud (performance ML, 0-100) por debajo del
 *                      umbral (default 80, configurable por empresa vía
 *                      `empresa.umbralesPublicaciones.scoreCalidad`).
 *                      Sin score = sin hallazgo (pausadas/finalizadas — se
 *                      declara en `excluidos.sinScore`).
 *   modificar_foto   → la foto principal NO tiene el tag good_quality_picture
 *                      de ML (decisión 11-jul: SOLO ese tag — thumbnail bueno
 *                      no alcanza).
 *
 * Solo publicaciones ACTIVAS generan hallazgos (una pausada no compite).
 *
 * Input (shapes del loader del agente — pubCanonicaALegacy con passthrough
 * de `datosCanal`/`facetas`, y campanasCuotas crudo por whereEmpresaId):
 *   {
 *     publicaciones: [{ id, itemId, title, price, permalink, thumbnail,
 *                       status, datosCanal, facetas, ... }],
 *     campanasCuotas: [{ categoryId, porListingType }],
 *     config: {}  // doc de empresa
 *   }
 *
 * Firma de salida: { secciones, analisis, excluidos }
 */

export const UMBRAL_SCORE_CALIDAD_DEFAULT = 80

// Tags de cuotas (misma regla que cuotasPorMlau de la página / ADR campañas):
// gold_pro = 6 cuotas base; tags Nx_campaign / *co-funded = cuotas activas.
const esTagCuota = (t) => /^\d+x_campaign$/.test(t) || /co[-_]funded$/i.test(t)

// Clave de producto a nivel catálogo (espejo de lib/publicacionesBandas.mlauKey).
const mlauKeyDe = (p) =>
  p?.datosCanal?.userProductId || p?.userProductId || ('__solo_' + (p?.itemId || p?.id || ''))

const esActiva = (p) => p?.status === 'active' || p?.estado === 'activa'

const itemIdDe = (p) => String(p?.itemId || p?.externalId || p?.id || '')

// Visitas de los últimos 30 días: ordenan las secciones que pierden conversión
// sobre el tráfico de HOY (foto y cuotas).
const visitas30dDe = (p) => p?._visitas30d ?? p?.datosCanal?.visitasRecientes?.last30 ?? 0

// Cuotas: lo más fuerte primero (la cuota del lote recorta el final, `agentes.js:1064`),
// por visitas y, a igual tráfico, por precio.
const porVisitasYPrecio = (a, b) => (b._visitas30d ?? 0) - (a._visitas30d ?? 0) || (b.price ?? 0) - (a.price ?? 0)

// Normaliza el hallazgo al shape que consumen tabla + recurso de tarea.
function baseItem(p) {
  return {
    id: itemIdDe(p),
    itemId: itemIdDe(p),
    _itemId: itemIdDe(p),
    title: p.title ?? p.titulo ?? null,
    price: p.price ?? p.precio ?? null,
    permalink: p.permalink ?? p.urlPublica ?? null,
    thumbnail: p.thumbnail ?? null,
    status: p.status ?? null,
  }
}

export function clasificarPublicaciones({ publicaciones = [], campanasCuotas = [], config = {} } = {}) {
  const umbralScore = Number(config?.umbralesPublicaciones?.scoreCalidad) > 0
    ? Number(config.umbralesPublicaciones.scoreCalidad)
    : UMBRAL_SCORE_CALIDAD_DEFAULT

  const activas = publicaciones.filter(esActiva)

  // ── mejorar_calidad: score < umbral (solo con score presente) ──
  const mejorar_calidad = []
  let sinScore = 0
  for (const p of activas) {
    const score = p?.datosCanal?.health?.score
    if (typeof score !== 'number') { sinScore++; continue }
    if (score >= umbralScore) continue
    const level = String(p?.datosCanal?.health?.level || '').toLowerCase() || null
    mejorar_calidad.push({
      ...baseItem(p),
      _score: score,
      _level: level,
      _motivo: `Score ${score} < ${umbralScore}${level ? ` (nivel ${level})` : ''}`,
    })
  }
  mejorar_calidad.sort((a, b) => (a._score ?? 0) - (b._score ?? 0)) // peor primero

  // ── modificar_foto: sin tag good_quality_picture (solo ese tag) ──
  // SPLIT por tráfico (decisión Mariano 11-jul): CON tráfico = quick win (la
  // foto mala la está viendo gente HOY, ordenada por visitas desc) · SIN
  // tráfico = backlog (prioridad baja). Ambas secciones convergen en el mismo
  // tipo `modificar_foto` — códigos compartidos, el dedupe hace lo suyo.
  const modificar_foto = []
  const modificar_foto_sin_trafico = []
  for (const p of activas) {
    const tags = new Set((p?.datosCanal?.tags || []).map(String))
    if (tags.has('good_quality_picture')) continue
    const visitas30d = visitas30dDe(p)
    const item = {
      ...baseItem(p),
      _visitas30d: visitas30d,
      _tieneThumbnailOk: tags.has('good_quality_thumbnail'),
      _motivo: visitas30d > 0
        ? `Foto principal sin good_quality_picture con ${visitas30d} visitas/30d`
        : 'Foto principal sin good_quality_picture (sin tráfico)',
    }
    if (visitas30d > 0) modificar_foto.push(item)
    else modificar_foto_sin_trafico.push(item)
  }
  modificar_foto.sort((a, b) => (b._visitas30d ?? 0) - (a._visitas30d ?? 0))
  // Sin tráfico no hay otra señal que el valor del ítem (molde: `sin_trafico`, traficoConversion.js).
  modificar_foto_sin_trafico.sort((a, b) => (b.price ?? 0) - (a.price ?? 0))

  // ── agregar_cuotas: grupo 'disp' por MLAU (Tiene / Puede agregar / No puede) ──
  // Universo = campanasCuotas (categoría × listing_type). Si la colección no
  // está cargada, 'disp' NO se puede calcular → sección vacía DECLARADA.
  const universoPorCategoria = new Map()
  for (const c of campanasCuotas) {
    if (c?.categoryId) universoPorCategoria.set(String(c.categoryId), c.porListingType || {})
  }
  const universoCuotasCargado = universoPorCategoria.size > 0

  const agregar_cuotas = []
  let mlausConCuotas = 0
  let mlausSinPosibilidad = 0
  if (universoCuotasCargado) {
    const hermanasPorMlau = new Map()
    for (const p of publicaciones) {
      const k = mlauKeyDe(p)
      if (!hermanasPorMlau.has(k)) hermanasPorMlau.set(k, [])
      hermanasPorMlau.get(k).push(p)
    }
    for (const [, hermanas] of hermanasPorMlau) {
      const universo = new Set()
      let tieneCuotas = false
      for (const h of hermanas) {
        const tags = (h?.datosCanal?.tags || []).map(String)
        if (tags.some(esTagCuota)) tieneCuotas = true
        if (h?.datosCanal?.listingTypeId === 'gold_pro') tieneCuotas = true // 6 cuotas base
        const porLT = universoPorCategoria.get(String(h?.datosCanal?.categoryId))
        const arr = porLT ? (porLT[h?.datosCanal?.listingTypeId] || []) : []
        for (const c of arr) if (c?.campaignId && c.campaignId !== 'no-campaign') universo.add(c.campaignId)
      }
      if (tieneCuotas) { mlausConCuotas++; continue }
      if (universo.size === 0) { mlausSinPosibilidad++; continue }
      // Grupo 'disp': representamos el MLAU con la hermana ACTIVA (si ninguna
      // está activa, no hay dónde accionar → se omite).
      const rep = hermanas.find(esActiva)
      if (!rep) continue
      agregar_cuotas.push({
        ...baseItem(rep),
        _visitas30d: visitas30dDe(rep),
        _hermanas: hermanas.length,
        _campanasDisponibles: universo.size,
        _motivo: `La categoría permite cuotas (${universo.size} campaña${universo.size > 1 ? 's' : ''}) y el producto no ofrece ninguna`,
      })
    }
  }

  // Falta de cuotas, como la foto mala, es conversión que se pierde sobre el tráfico actual.
  agregar_cuotas.sort(porVisitasYPrecio)

  return {
    secciones: {
      agregar_cuotas,
      mejorar_calidad,
      modificar_foto,
      modificar_foto_sin_trafico,
    },
    analisis: {
      kpis: {
        publicaciones: publicaciones.length,
        activas: activas.length,
        cuotasDisponibles: agregar_cuotas.length,
        calidadBaja: mejorar_calidad.length,
        fotoMejorable: modificar_foto.length,
        fotoMejorableSinTrafico: modificar_foto_sin_trafico.length,
      },
    },
    excluidos: {
      umbralScoreEfectivo: umbralScore,
      sinScore,
      universoCuotasCargado,
      mlausConCuotas,
      mlausSinPosibilidad,
    },
  }
}
